import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { RaceContestantComplete } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputDateTime,
    DateComp,
    InputNullableInt,
    ValidatedInt,
    InputText,
    ValidatedText,
    InputNullableFloat,
    ValidatedFloat,
    InputInt,
    InputNullableDateTime,
    InputSelect,
    DataSourceLookup,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import { useCommonStore } from '@/stores';
import { storeToRefs } from 'pinia';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';
import { useValidator } from '@/composeables';

export class RaceContestantCompleteGuiDef extends GuiDef<RaceContestantComplete> {
    constructor() {
        super('RaceContestantComplete');

        const Common = storeToRefs(useCommonStore());
        const ClubsSource = computed(() => {
            return Common.ClubsAsSelectLine.value;
        });
        const TeamsSource = computed(() => {
            return Common.TeamsAsSelectLine.value;
        });
        const ContestantStatusSource = computed(() => {
            return Common.ContestantStatusAsSelectLine.value;
        });

        const Firstname = new FieldDef<RaceContestantComplete>();
        Firstname.Name = 'Firstname';
        Firstname.FieldName = 'Firstname';
        Firstname.DefinedFor = 'Both';
        Firstname.TranslationKey = 'racecontestantcomplete.firstname';
        Firstname.Editable = false;
        Firstname.Filterable = false;
        Firstname.Sortable = true;
        Firstname.CssClasses = '';
        Firstname.value = (racecontestantcomplete) => racecontestantcomplete.Firstname;
        Firstname.setValue = () => {};
        Firstname.Validator = null;
        Firstname.SortDirection = undefined;
        Firstname.cellClassRules = undefined;
        Firstname.MinWidth = undefined;
        Firstname.MaxWidth = undefined;
        Firstname.Tooltip = null;
        this.Fields.push(Firstname);

        const Surname = new FieldDef<RaceContestantComplete>();
        Surname.Name = 'Surname';
        Surname.FieldName = 'Surname';
        Surname.DefinedFor = 'Both';
        Surname.TranslationKey = 'racecontestantcomplete.surname';
        Surname.Editable = false;
        Surname.Filterable = false;
        Surname.Sortable = true;
        Surname.CssClasses = '';
        Surname.value = (racecontestantcomplete) => racecontestantcomplete.Surname;
        Surname.setValue = () => {};
        Surname.Validator = null;
        Surname.SortDirection = undefined;
        Surname.cellClassRules = undefined;
        Surname.MinWidth = undefined;
        Surname.MaxWidth = undefined;
        Surname.Tooltip = null;
        this.Fields.push(Surname);

        const RelayTeamName = new FieldDef<RaceContestantComplete>();
        RelayTeamName.Name = 'RelayTeamName';
        RelayTeamName.FieldName = 'RelayTeamName';
        RelayTeamName.DefinedFor = 'Both';
        RelayTeamName.TranslationKey = 'racecontestantcomplete.relayteamname';
        RelayTeamName.Editable = false;
        RelayTeamName.Filterable = true;
        RelayTeamName.Sortable = true;
        RelayTeamName.CssClasses = '';
        RelayTeamName.value = (racecontestantcomplete) => racecontestantcomplete.RelayTeamName;
        RelayTeamName.setValue = () => {};
        RelayTeamName.Validator = null;
        RelayTeamName.SortDirection = undefined;
        RelayTeamName.cellClassRules = undefined;
        RelayTeamName.MinWidth = undefined;
        RelayTeamName.MaxWidth = undefined;
        RelayTeamName.Tooltip = null;
        this.Fields.push(RelayTeamName);

        const Birthday = new FieldDef<RaceContestantComplete>();
        Birthday.Name = 'Birthday';
        Birthday.FieldName = 'Birthday';
        Birthday.DefinedFor = 'Both';
        Birthday.TranslationKey = 'racecontestantcomplete.birthday';
        Birthday.Editable = false;
        Birthday.Filterable = true;
        Birthday.Sortable = true;
        Birthday.CssClasses = '';
        Birthday.value = (racecontestantcomplete) => racecontestantcomplete.Birthday;
        Birthday.setValue = () => {};
        Birthday.DetailEditor = markRaw(InputDateTime);
        Birthday.DetailEditorProps = { format: 'DATE' as DateType, readonly: true };
        Birthday.CellEditor = markRaw(InputDateTime);
        Birthday.CellEditorProps = { format: 'DATE' as DateType, readonly: true };
        Birthday.CellRenderer = markRaw(DateComp);
        Birthday.CellRendererProps = { format: 'DATE' as DateType, readonly: true };
        Birthday.Validator = null;
        Birthday.SortDirection = undefined;
        Birthday.cellClassRules = undefined;
        Birthday.MinWidth = 135;
        Birthday.MaxWidth = 135;
        Birthday.Tooltip = null;
        this.Fields.push(Birthday);

        const BibValidator = new ValidatorDef({ Min: 1, Max: null, Required: false, Type: undefined });
        const Bib = new FieldDef<RaceContestantComplete>();
        Bib.Name = 'Bib';
        Bib.FieldName = 'Bib';
        Bib.DefinedFor = 'Both';
        Bib.TranslationKey = 'racecontestantcomplete.bib';
        Bib.Editable = true;
        Bib.Filterable = false;
        Bib.Sortable = true;
        Bib.CssClasses = '';
        Bib.value = (racecontestantcomplete) => racecontestantcomplete.Bib;
        Bib.setValue = (racecontestantcomplete, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantcomplete.Bib = value;
        };
        Bib.DetailEditor = markRaw(InputNullableInt);
        Bib.DetailEditorProps = {};
        Bib.CellEditor = markRaw(InputNullableInt);
        Bib.CellEditorProps = {};
        Bib.CellRenderer = markRaw(ValidatedInt);
        Bib.CellRendererProps = {};
        Bib.Validator = new ValidatorDef({ Min: 1, Max: null, Required: false, Type: undefined });
        Bib.SortDirection = undefined;
        Bib.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(BibValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(BibValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        Bib.MinWidth = 75;
        Bib.MaxWidth = 75;
        Bib.Tooltip = null;
        this.Fields.push(Bib);

        const Chip1 = new FieldDef<RaceContestantComplete>();
        Chip1.Name = 'Chip1';
        Chip1.FieldName = 'Chip1';
        Chip1.DefinedFor = 'Both';
        Chip1.TranslationKey = 'racecontestantcomplete.chip1';
        Chip1.Editable = true;
        Chip1.Filterable = false;
        Chip1.Sortable = true;
        Chip1.CssClasses = '';
        Chip1.value = (racecontestantcomplete) => racecontestantcomplete.Chip1;
        Chip1.setValue = (racecontestantcomplete, value) => (racecontestantcomplete.Chip1 = value);
        Chip1.DetailEditor = markRaw(InputText);
        Chip1.DetailEditorProps = { modelModifiers: { uppercase: true } };
        Chip1.CellEditor = markRaw(InputText);
        Chip1.CellEditorProps = { modelModifiers: { uppercase: true } };
        Chip1.CellRenderer = markRaw(ValidatedText);
        Chip1.CellRendererProps = { modelModifiers: { uppercase: true } };
        Chip1.Validator = null;
        Chip1.SortDirection = undefined;
        Chip1.cellClassRules = undefined;
        Chip1.MinWidth = 100;
        Chip1.MaxWidth = 100;
        Chip1.Tooltip = null;
        this.Fields.push(Chip1);

        const Chip2 = new FieldDef<RaceContestantComplete>();
        Chip2.Name = 'Chip2';
        Chip2.FieldName = 'Chip2';
        Chip2.DefinedFor = 'Both';
        Chip2.TranslationKey = 'racecontestantcomplete.chip2';
        Chip2.Editable = true;
        Chip2.Filterable = false;
        Chip2.Sortable = true;
        Chip2.CssClasses = '';
        Chip2.value = (racecontestantcomplete) => racecontestantcomplete.Chip2;
        Chip2.setValue = (racecontestantcomplete, value) => (racecontestantcomplete.Chip2 = value);
        Chip2.DetailEditor = markRaw(InputText);
        Chip2.DetailEditorProps = { modelModifiers: { uppercase: true } };
        Chip2.CellEditor = markRaw(InputText);
        Chip2.CellEditorProps = { modelModifiers: { uppercase: true } };
        Chip2.CellRenderer = markRaw(ValidatedText);
        Chip2.CellRendererProps = { modelModifiers: { uppercase: true } };
        Chip2.Validator = null;
        Chip2.SortDirection = undefined;
        Chip2.cellClassRules = undefined;
        Chip2.MinWidth = 100;
        Chip2.MaxWidth = 100;
        Chip2.Tooltip = null;
        this.Fields.push(Chip2);

        const Seeding = new FieldDef<RaceContestantComplete>();
        Seeding.Name = 'Seeding';
        Seeding.FieldName = 'Seeding';
        Seeding.DefinedFor = 'Both';
        Seeding.TranslationKey = 'racecontestantcomplete.seeding';
        Seeding.Editable = true;
        Seeding.Filterable = false;
        Seeding.Sortable = true;
        Seeding.CssClasses = '';
        Seeding.value = (racecontestantcomplete) => racecontestantcomplete.Seeding;
        Seeding.setValue = (racecontestantcomplete, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantcomplete.Seeding = value;
        };
        Seeding.DetailEditor = markRaw(InputNullableFloat);
        Seeding.DetailEditorProps = {};
        Seeding.CellEditor = markRaw(InputNullableFloat);
        Seeding.CellEditorProps = {};
        Seeding.CellRenderer = markRaw(ValidatedFloat);
        Seeding.CellRendererProps = {};
        Seeding.Validator = null;
        Seeding.SortDirection = undefined;
        Seeding.cellClassRules = undefined;
        Seeding.MinWidth = 100;
        Seeding.MaxWidth = 100;
        Seeding.Tooltip = null;
        this.Fields.push(Seeding);

        const SeedingGroup = new FieldDef<RaceContestantComplete>();
        SeedingGroup.Name = 'SeedingGroup';
        SeedingGroup.FieldName = 'SeedingGroup';
        SeedingGroup.DefinedFor = 'Both';
        SeedingGroup.TranslationKey = 'racecontestantcomplete.seedinggroup';
        SeedingGroup.Editable = true;
        SeedingGroup.Filterable = false;
        SeedingGroup.Sortable = true;
        SeedingGroup.CssClasses = '';
        SeedingGroup.value = (racecontestantcomplete) => racecontestantcomplete.SeedingGroup;
        SeedingGroup.setValue = (racecontestantcomplete, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            racecontestantcomplete.SeedingGroup = value;
        };
        SeedingGroup.DetailEditor = markRaw(InputInt);
        SeedingGroup.DetailEditorProps = {};
        SeedingGroup.CellEditor = markRaw(InputInt);
        SeedingGroup.CellEditorProps = {};
        SeedingGroup.CellRenderer = markRaw(ValidatedInt);
        SeedingGroup.CellRendererProps = {};
        SeedingGroup.Validator = null;
        SeedingGroup.SortDirection = undefined;
        SeedingGroup.cellClassRules = undefined;
        SeedingGroup.MinWidth = undefined;
        SeedingGroup.MaxWidth = undefined;
        SeedingGroup.Tooltip = null;
        this.Fields.push(SeedingGroup);

        const StartingTimeValidator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        const StartingTime = new FieldDef<RaceContestantComplete>();
        StartingTime.Name = 'StartingTime';
        StartingTime.FieldName = 'StartingTime';
        StartingTime.DefinedFor = 'Both';
        StartingTime.TranslationKey = 'racecontestantcomplete.startingtime';
        StartingTime.Editable = true;
        StartingTime.Filterable = true;
        StartingTime.Sortable = true;
        StartingTime.CssClasses = '';
        StartingTime.value = (racecontestantcomplete) => racecontestantcomplete.StartingTime;
        StartingTime.setValue = (racecontestantcomplete, value) => (racecontestantcomplete.StartingTime = value);
        StartingTime.DetailEditor = markRaw(InputNullableDateTime);
        StartingTime.DetailEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartingTime.CellEditor = markRaw(InputNullableDateTime);
        StartingTime.CellEditorProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartingTime.CellRenderer = markRaw(DateComp);
        StartingTime.CellRendererProps = { format: 'DATETIME' as DateType, prefillUsingEventDate: true };
        StartingTime.Validator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'eventactivetime' });
        StartingTime.SortDirection = undefined;
        StartingTime.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(StartingTimeValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(StartingTimeValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        StartingTime.MinWidth = 180;
        StartingTime.MaxWidth = undefined;
        StartingTime.Tooltip = null;
        this.Fields.push(StartingTime);

        const RaceName = new FieldDef<RaceContestantComplete>();
        RaceName.Name = 'RaceName';
        RaceName.FieldName = 'RaceName';
        RaceName.DefinedFor = 'Both';
        RaceName.TranslationKey = 'racecontestantcomplete.racename';
        RaceName.Editable = false;
        RaceName.Filterable = true;
        RaceName.Sortable = true;
        RaceName.CssClasses = '';
        RaceName.value = (racecontestantcomplete) => racecontestantcomplete.RaceName;
        RaceName.setValue = () => {};
        RaceName.DetailEditor = markRaw(InputText);
        RaceName.DetailEditorProps = {};
        RaceName.CellEditor = markRaw(InputText);
        RaceName.CellEditorProps = {};
        RaceName.CellRenderer = markRaw(ValidatedText);
        RaceName.CellRendererProps = {};
        RaceName.Validator = null;
        RaceName.SortDirection = undefined;
        RaceName.cellClassRules = undefined;
        RaceName.MinWidth = undefined;
        RaceName.MaxWidth = undefined;
        RaceName.Tooltip = null;
        this.Fields.push(RaceName);

        const GroupName = new FieldDef<RaceContestantComplete>();
        GroupName.Name = 'GroupName';
        GroupName.FieldName = 'GroupName';
        GroupName.DefinedFor = 'Both';
        GroupName.TranslationKey = 'racecontestantcomplete.groupname';
        GroupName.Editable = false;
        GroupName.Filterable = true;
        GroupName.Sortable = true;
        GroupName.CssClasses = '';
        GroupName.value = (racecontestantcomplete) => racecontestantcomplete.GroupName;
        GroupName.setValue = () => {};
        GroupName.DetailEditor = markRaw(InputText);
        GroupName.DetailEditorProps = {};
        GroupName.CellEditor = markRaw(InputText);
        GroupName.CellEditorProps = {};
        GroupName.CellRenderer = markRaw(ValidatedText);
        GroupName.CellRendererProps = {};
        GroupName.Validator = null;
        GroupName.SortDirection = undefined;
        GroupName.cellClassRules = undefined;
        GroupName.MinWidth = undefined;
        GroupName.MaxWidth = undefined;
        GroupName.Tooltip = null;
        this.Fields.push(GroupName);

        const CategoryNameValidator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'contestant' });
        const CategoryName = new FieldDef<RaceContestantComplete>();
        CategoryName.Name = 'CategoryName';
        CategoryName.FieldName = 'CategoryName';
        CategoryName.DefinedFor = 'Both';
        CategoryName.TranslationKey = 'racecontestantcomplete.categoryname';
        CategoryName.Editable = false;
        CategoryName.Filterable = true;
        CategoryName.Sortable = true;
        CategoryName.CssClasses = '';
        CategoryName.value = (racecontestantcomplete) => racecontestantcomplete.CategoryName;
        CategoryName.setValue = () => {};
        CategoryName.DetailEditor = markRaw(InputText);
        CategoryName.DetailEditorProps = {};
        CategoryName.CellEditor = markRaw(InputText);
        CategoryName.CellEditorProps = {};
        CategoryName.CellRenderer = markRaw(ValidatedText);
        CategoryName.CellRendererProps = {};
        CategoryName.Validator = new ValidatorDef({ Min: null, Max: null, Required: null, Type: 'contestant' });
        CategoryName.SortDirection = undefined;
        CategoryName.cellClassRules = [
            {
                className: 'grid-cell-warning',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(CategoryNameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'warning';
                },
            },
            {
                className: 'grid-cell-error',
                func: (cell: any) => {
                    const { ValidateMessage } = useValidator(CategoryNameValidator ?? null, cell);
                    return ValidateMessage.value?.type === 'danger';
                },
            },
        ];
        CategoryName.MinWidth = undefined;
        CategoryName.MaxWidth = undefined;
        CategoryName.Tooltip = null;
        this.Fields.push(CategoryName);

        const ClubID = new FieldDef<RaceContestantComplete>();
        ClubID.Name = 'ClubID';
        ClubID.FieldName = 'ClubID';
        ClubID.DefinedFor = 'Both';
        ClubID.TranslationKey = 'racecontestantcomplete.clubid';
        ClubID.Editable = true;
        ClubID.Filterable = false;
        ClubID.Sortable = true;
        ClubID.CssClasses = '';
        ClubID.value = (racecontestantcomplete) => racecontestantcomplete.ClubID;
        ClubID.setValue = (racecontestantcomplete, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantcomplete.ClubID = value;
        };
        ClubID.CellEditor = markRaw(InputSelect);
        ClubID.CellEditorProps = { showSearch: true, virtualScrollerMaxItems: 30, datasource: { mapsource: ClubsSource }, autoOpen: true };
        // @ts-expect-error @ts-ignore
        ClubID.CellRenderer = markRaw(DataSourceLookup);
        ClubID.CellRendererProps = { datasource: { mapsource: ClubsSource } };
        ClubID.ExportValueFormatter = (obj: RaceContestantComplete) => {
            return ClubsSource.value.get(obj.ClubID)?.text;
        };
        ClubID.Validator = null;
        ClubID.SortDirection = undefined;
        ClubID.cellClassRules = undefined;
        ClubID.MinWidth = undefined;
        ClubID.MaxWidth = undefined;
        ClubID.Tooltip = null;
        this.Fields.push(ClubID);

        const TeamID = new FieldDef<RaceContestantComplete>();
        TeamID.Name = 'TeamID';
        TeamID.FieldName = 'TeamID';
        TeamID.DefinedFor = 'Both';
        TeamID.TranslationKey = 'racecontestantcomplete.teamid';
        TeamID.Editable = true;
        TeamID.Filterable = false;
        TeamID.Sortable = true;
        TeamID.CssClasses = '';
        TeamID.value = (racecontestantcomplete) => racecontestantcomplete.TeamID;
        TeamID.setValue = (racecontestantcomplete, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            racecontestantcomplete.TeamID = value;
        };
        TeamID.CellEditor = markRaw(InputSelect);
        TeamID.CellEditorProps = { showSearch: true, virtualScrollerMaxItems: 30, datasource: { mapsource: TeamsSource }, autoOpen: true };
        // @ts-expect-error @ts-ignore
        TeamID.CellRenderer = markRaw(DataSourceLookup);
        TeamID.CellRendererProps = { datasource: { mapsource: TeamsSource } };
        TeamID.ExportValueFormatter = (obj: RaceContestantComplete) => {
            return TeamsSource.value.get(obj.TeamID)?.text;
        };
        TeamID.Validator = null;
        TeamID.SortDirection = undefined;
        TeamID.cellClassRules = undefined;
        TeamID.MinWidth = undefined;
        TeamID.MaxWidth = undefined;
        TeamID.Tooltip = null;
        this.Fields.push(TeamID);

        const CompanyClub = new FieldDef<RaceContestantComplete>();
        CompanyClub.Name = 'CompanyClub';
        CompanyClub.FieldName = 'CompanyClub';
        CompanyClub.DefinedFor = 'Both';
        CompanyClub.TranslationKey = 'racecontestantcomplete.companyclub';
        CompanyClub.Editable = true;
        CompanyClub.Filterable = true;
        CompanyClub.Sortable = true;
        CompanyClub.CssClasses = '';
        CompanyClub.value = (racecontestantcomplete) => racecontestantcomplete.CompanyClub;
        CompanyClub.setValue = (racecontestantcomplete, value) => (racecontestantcomplete.CompanyClub = value);
        CompanyClub.DetailEditor = markRaw(InputText);
        CompanyClub.DetailEditorProps = {};
        CompanyClub.CellEditor = markRaw(InputText);
        CompanyClub.CellEditorProps = {};
        CompanyClub.CellRenderer = markRaw(ValidatedText);
        CompanyClub.CellRendererProps = {};
        CompanyClub.Validator = null;
        CompanyClub.SortDirection = undefined;
        CompanyClub.cellClassRules = undefined;
        CompanyClub.MinWidth = undefined;
        CompanyClub.MaxWidth = undefined;
        CompanyClub.Tooltip = null;
        this.Fields.push(CompanyClub);

        const ContestantStatusID = new FieldDef<RaceContestantComplete>();
        ContestantStatusID.Name = 'ContestantStatusID';
        ContestantStatusID.FieldName = 'ContestantStatusID';
        ContestantStatusID.DefinedFor = 'Both';
        ContestantStatusID.TranslationKey = 'racecontestantcomplete.contestantstatusid';
        ContestantStatusID.Editable = true;
        ContestantStatusID.Filterable = true;
        ContestantStatusID.Sortable = true;
        ContestantStatusID.CssClasses = '';
        ContestantStatusID.value = (racecontestantcomplete) => racecontestantcomplete.ContestantStatusID;
        ContestantStatusID.setValue = (racecontestantcomplete, value) => {
            if (value === null || value === undefined || value === '' || isNaN(value)) {
                value = 0;
            }
            racecontestantcomplete.ContestantStatusID = value;
        };
        ContestantStatusID.DetailEditor = markRaw(InputSelect);
        ContestantStatusID.DetailEditorProps = { virtualScrollerMaxItems: 30, datasource: { mapsource: ContestantStatusSource } };
        ContestantStatusID.CellEditor = markRaw(InputSelect);
        ContestantStatusID.CellEditorProps = {
            virtualScrollerMaxItems: 30,
            datasource: { mapsource: ContestantStatusSource },
            autoOpen: true,
        };
        // @ts-expect-error @ts-ignore
        ContestantStatusID.CellRenderer = markRaw(DataSourceLookup);
        ContestantStatusID.CellRendererProps = { datasource: { mapsource: ContestantStatusSource } };
        ContestantStatusID.ExportValueFormatter = (obj: RaceContestantComplete) => {
            return ContestantStatusSource.value.get(obj.ContestantStatusID)?.text;
        };
        ContestantStatusID.Validator = null;
        ContestantStatusID.SortDirection = undefined;
        ContestantStatusID.cellClassRules = undefined;
        ContestantStatusID.MinWidth = undefined;
        ContestantStatusID.MaxWidth = undefined;
        ContestantStatusID.Tooltip = null;
        this.Fields.push(ContestantStatusID);
    }

    public async LoadDependencies() {
        await useCommonStore().EnsureLoaded(['Clubs']);
        await useCommonStore().EnsureLoaded(['Teams']);
        await useCommonStore().EnsureLoaded(['ContestantStatus']);
    }
}
